<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import FunnelsAPI from 'dashboard/api/funnels';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  contactId: {
    type: Number,
    required: true,
  },
  conversationId: {
    type: [Number, String],
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);
const isAddingCard = ref(false);
const isMovingCard = ref(false);
const funnelItems = ref([]);
const funnels = ref([]);
const showAddModal = ref(false);
const showMoveModal = ref(false);
const selectedFunnel = ref(null);
const selectedStage = ref(null);
const stages = ref([]);
const itemToMove = ref(null);
const moveStages = ref([]);

const accountId = computed(() => store.getters.getCurrentAccountId);
const contact = computed(() => store.getters['contacts/getContact'](props.contactId));

const fetchFunnelItems = async () => {
  if (!props.contactId) return;

  isLoading.value = true;
  try {
    const response = await FunnelsAPI.getContactItems(props.contactId);
    funnelItems.value = response.data;
  } catch (error) {
    console.error('Failed to fetch funnel items:', error);
  } finally {
    isLoading.value = false;
  }
};

const fetchFunnels = async () => {
  try {
    const response = await FunnelsAPI.get();
    funnels.value = response.data;
  } catch (error) {
    console.error('Failed to fetch funnels:', error);
  }
};

const fetchStages = async funnelId => {
  if (!funnelId) {
    stages.value = [];
    return;
  }
  try {
    const response = await FunnelsAPI.getStages(funnelId);
    stages.value = response.data;
  } catch (error) {
    console.error('Failed to fetch stages:', error);
  }
};

const openAddModal = async () => {
  showAddModal.value = true;
  await fetchFunnels();
  // Se tiver apenas 1 board, seleciona automaticamente
  if (funnels.value.length === 1) {
    selectedFunnel.value = funnels.value[0].id;
    await fetchStages(selectedFunnel.value);
  }
};

const closeAddModal = () => {
  showAddModal.value = false;
  selectedFunnel.value = null;
  selectedStage.value = null;
  stages.value = [];
};

const onFunnelChange = () => {
  selectedStage.value = null;
  if (selectedFunnel.value) {
    fetchStages(selectedFunnel.value);
  } else {
    stages.value = [];
  }
};

const hasSingleFunnel = computed(() => funnels.value.length === 1);

const selectStageDirectly = async stageId => {
  selectedStage.value = stageId;
  await addToFunnel();
};

const addToFunnel = async () => {
  if (!selectedFunnel.value || !selectedStage.value) {
    useAlert(t('CONTACT_PANEL.FUNNEL_ITEMS.SELECT_FUNNEL_STAGE'));
    return;
  }

  isAddingCard.value = true;
  try {
    const contactName = contact.value?.name || contact.value?.phone_number || `Contato #${props.contactId}`;
    await FunnelsAPI.createItem(selectedFunnel.value, {
      funnel_stage_id: selectedStage.value,
      contact_id: props.contactId,
      conversation_id: props.conversationId,
      title: contactName,
    });
    useAlert(t('CONTACT_PANEL.FUNNEL_ITEMS.CARD_ADDED'));
    closeAddModal();
    fetchFunnelItems();
  } catch (error) {
    const errorMsg =
      error.response?.data?.error ||
      t('CONTACT_PANEL.FUNNEL_ITEMS.ADD_ERROR');
    useAlert(errorMsg);
  } finally {
    isAddingCard.value = false;
  }
};

const goToFunnel = item => {
  const route = `/app/accounts/${accountId.value}/funnels/${item.funnel_id}`;
  window.open(route, '_blank');
};

const removeFromFunnel = async item => {
  if (!confirm(t('CONTACT_PANEL.FUNNEL_ITEMS.CONFIRM_REMOVE'))) return;

  try {
    await FunnelsAPI.deleteItem(item.funnel_id, item.id);
    useAlert(t('CONTACT_PANEL.FUNNEL_ITEMS.CARD_REMOVED'));
    fetchFunnelItems();
  } catch (error) {
    useAlert(t('CONTACT_PANEL.FUNNEL_ITEMS.REMOVE_ERROR'));
  }
};

// Move stage functions
const openMoveModal = async item => {
  itemToMove.value = item;
  showMoveModal.value = true;
  try {
    const response = await FunnelsAPI.getStages(item.funnel_id);
    moveStages.value = response.data.filter(stage => stage.id !== item.stage_id);
  } catch (error) {
    console.error('Failed to fetch stages for move:', error);
    moveStages.value = [];
  }
};

const closeMoveModal = () => {
  showMoveModal.value = false;
  itemToMove.value = null;
  moveStages.value = [];
};

const moveToStage = async stageId => {
  if (!itemToMove.value || !stageId) return;

  isMovingCard.value = true;
  try {
    await FunnelsAPI.moveItem(itemToMove.value.funnel_id, itemToMove.value.id, stageId);
    useAlert(t('CONTACT_PANEL.FUNNEL_ITEMS.CARD_MOVED'));
    closeMoveModal();
    fetchFunnelItems();
  } catch (error) {
    useAlert(t('CONTACT_PANEL.FUNNEL_ITEMS.MOVE_ERROR'));
  } finally {
    isMovingCard.value = false;
  }
};

watch(
  () => props.contactId,
  newId => {
    if (newId) {
      fetchFunnelItems();
    }
  }
);

onMounted(() => {
  fetchFunnelItems();
});
</script>

<template>
  <div class="contact-funnel-items">
    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center py-4">
      <Spinner size="small" />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="funnelItems.length === 0"
      class="text-sm text-n-slate-11 py-2"
    >
      {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.NO_ITEMS') }}
    </div>

    <!-- List of funnel items -->
    <div v-else class="flex flex-col gap-2">
      <div
        v-for="item in funnelItems"
        :key="item.id"
        class="flex items-center justify-between p-2 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors"
      >
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span
              class="w-2 h-2 rounded-full flex-shrink-0"
              :style="{ backgroundColor: item.stage_color || '#6366f1' }"
            />
            <span class="text-sm font-medium text-n-slate-12 truncate">
              {{ item.funnel_title }}
            </span>
          </div>
          <div class="text-xs text-n-slate-11 mt-0.5 pl-4">
            {{ item.stage_name }}
          </div>
        </div>
        <div class="flex items-center gap-1">
          <button
            class="p-1 rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-brand"
            :title="$t('CONTACT_PANEL.FUNNEL_ITEMS.MOVE_STAGE')"
            @click="openMoveModal(item)"
          >
            <fluent-icon icon="arrow-sort" size="14" />
          </button>
          <button
            class="p-1 rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12"
            :title="$t('CONTACT_PANEL.FUNNEL_ITEMS.VIEW_BOARD')"
            @click="goToFunnel(item)"
          >
            <fluent-icon icon="open" size="14" />
          </button>
          <button
            class="p-1 rounded hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-ruby-11"
            :title="$t('CONTACT_PANEL.FUNNEL_ITEMS.REMOVE')"
            @click="removeFromFunnel(item)"
          >
            <fluent-icon icon="delete" size="14" />
          </button>
        </div>
      </div>
    </div>

    <!-- Add button -->
    <button
      class="flex items-center gap-1 mt-3 text-sm text-n-brand hover:text-n-brand-dark transition-colors"
      @click="openAddModal"
    >
      <fluent-icon icon="add" size="14" />
      {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.ADD_TO_BOARD') }}
    </button>

    <!-- Add Modal -->
    <woot-modal
      :show="showAddModal"
      :on-close="closeAddModal"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium text-n-slate-12 mb-4">
          {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.ADD_TO_BOARD') }}
        </h3>

        <div class="flex flex-col gap-4">
          <!-- Funnel select (só mostra se tiver mais de 1 board) -->
          <div v-if="!hasSingleFunnel">
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.SELECT_BOARD') }}
            </label>
            <select
              v-model="selectedFunnel"
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:border-n-brand focus:outline-none"
              @change="onFunnelChange"
            >
              <option :value="null">
                {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.SELECT_PLACEHOLDER') }}
              </option>
              <option v-for="funnel in funnels" :key="funnel.id" :value="funnel.id">
                {{ funnel.title }}
              </option>
            </select>
          </div>

          <!-- Etapas em lista (quando só tem 1 board) -->
          <div v-if="hasSingleFunnel && stages.length > 0">
            <label class="block text-sm font-medium text-n-slate-11 mb-2">
              {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.SELECT_STAGE') }}
            </label>
            <div class="flex flex-col gap-1">
              <button
                v-for="stage in stages"
                :key="stage.id"
                class="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-2 hover:border-n-brand transition-colors text-left"
                :disabled="isAddingCard"
                @click="selectStageDirectly(stage.id)"
              >
                <span
                  class="w-3 h-3 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: stage.color || '#6366f1' }"
                />
                <span class="text-sm">{{ stage.name }}</span>
              </button>
            </div>
          </div>

          <!-- Stage select dropdown (quando tem mais de 1 board) -->
          <div v-if="!hasSingleFunnel">
            <label class="block text-sm font-medium text-n-slate-11 mb-1">
              {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.SELECT_STAGE') }}
            </label>
            <select
              v-model="selectedStage"
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 focus:border-n-brand focus:outline-none"
              :disabled="!selectedFunnel"
            >
              <option :value="null">
                {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.SELECT_PLACEHOLDER') }}
              </option>
              <option v-for="stage in stages" :key="stage.id" :value="stage.id">
                {{ stage.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Botões só aparecem quando tem mais de 1 board -->
        <div v-if="!hasSingleFunnel" class="flex justify-end gap-2 mt-6">
          <woot-button variant="clear" @click="closeAddModal">
            {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.CANCEL') }}
          </woot-button>
          <woot-button
            :is-loading="isAddingCard"
            :disabled="!selectedFunnel || !selectedStage"
            @click="addToFunnel"
          >
            {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.ADD') }}
          </woot-button>
        </div>

        <!-- Botão cancelar quando tem 1 board -->
        <div v-else class="flex justify-end mt-4">
          <woot-button variant="clear" @click="closeAddModal">
            {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.CANCEL') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>

    <!-- Move Stage Modal -->
    <woot-modal
      :show="showMoveModal"
      :on-close="closeMoveModal"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium text-n-slate-12 mb-2">
          {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.MOVE_STAGE') }}
        </h3>
        <p v-if="itemToMove" class="text-sm text-n-slate-11 mb-4">
          {{ itemToMove.funnel_title }} - {{ itemToMove.stage_name }}
        </p>

        <div class="flex flex-col gap-4">
          <div v-if="moveStages.length > 0">
            <label class="block text-sm font-medium text-n-slate-11 mb-2">
              {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.SELECT_NEW_STAGE') }}
            </label>
            <div class="flex flex-col gap-1">
              <button
                v-for="stage in moveStages"
                :key="stage.id"
                class="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 hover:bg-n-alpha-2 hover:border-n-brand transition-colors text-left"
                :disabled="isMovingCard"
                @click="moveToStage(stage.id)"
              >
                <span
                  class="w-3 h-3 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: stage.color || '#6366f1' }"
                />
                <span class="text-sm">{{ stage.name }}</span>
              </button>
            </div>
          </div>

          <div v-else class="text-sm text-n-slate-11 py-4 text-center">
            {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.NO_OTHER_STAGES') }}
          </div>
        </div>

        <div class="flex justify-end mt-4">
          <woot-button variant="clear" @click="closeMoveModal">
            {{ $t('CONTACT_PANEL.FUNNEL_ITEMS.CANCEL') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>
